import { useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";
import api from "@/lib/api";
import { PaperPlaneRight, Sparkle } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DataTag } from "@/components/widgets";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Give me a read on NVDA and AAPL",
  "What is the current market regime?",
  "Compare the 5-day forecast for TSLA vs MSFT",
  "What are the biggest risk factors for AMZN right now?",
];

export default function Assistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    api.get("/assistant/history?session_id=default")
      .then((r) => setMessages((cur) => (cur.length ? cur : r.data.messages)))
      .catch(() => {});
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, streaming]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/assistant/chat`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, session_id: "default" }),
      });
      if (!res.ok) {
        let detail = "Something went wrong";
        try { const j = await res.json(); detail = j.detail || detail; } catch (_) {}
        setMessages((m) => { if (!m.length) return m; const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: detail }; return c; });
        toast.error(detail);
        setStreaming(false);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data: /, "").trim();
          if (!line) continue;
          try {
            const d = JSON.parse(line);
            if (d.delta) setMessages((m) => { if (!m.length) return m; const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + d.delta }; return c; });
            if (d.error) setMessages((m) => { if (!m.length) return m; const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + `\n[error: ${d.error}]` }; return c; });
          } catch (e) {}
        }
      }
    } catch (e) {
      setMessages((m) => { if (!m.length) return m; const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: "Connection error." }; return c; });
    } finally { setStreaming(false); }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-6rem)]">
      <div className="flex items-end justify-between">
        <div><div className="label-xs">User Intelligence</div><h1 className="mt-1 font-heading text-3xl font-bold tracking-tighter flex items-center gap-2"><Sparkle size={26} className="text-volt" /> AI Assistant</h1></div>
        <DataTag kind="AI" />
      </div>

      <div className="mt-4 flex-1 overflow-y-auto border border-border bg-card p-5 space-y-4" data-testid="chat-window">
        {messages.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <p className="max-w-md text-sm text-muted-foreground">Ask about any stock, the market regime, forecasts, or risk. The assistant only uses live intelligence data — it never invents numbers.</p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-lg">
              {SUGGESTIONS.map((s) => (
                <button key={s} data-testid="suggestion" onClick={() => send(s)} className="border border-border p-3 text-left text-xs text-muted-foreground hover:text-white hover:border-volt/40 transition-colors duration-200 rounded-sm">{s}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] px-4 py-3 rounded-sm text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-volt/15 border border-volt/30" : "bg-background border border-border"}`}>
              {m.role === "assistant" && <div className="label-xs mb-1.5 text-volt">MarketPilot AI</div>}
              {m.content || (streaming && i === messages.length - 1 ? <span className="animate-pulse">▍</span> : "")}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-3 flex gap-2">
        <Input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)} disabled={streaming}
          placeholder="Ask about a stock, regime, forecast, or risk…" className="bg-card rounded-sm h-11" />
        <Button data-testid="chat-send-btn" type="submit" disabled={streaming || !input.trim()} className="bg-volt hover:bg-volt/90 rounded-sm h-11 px-4"><PaperPlaneRight size={18} weight="fill" /></Button>
      </form>
      <p className="mt-2 mono text-[10px] text-muted-foreground text-center">Informational / educational only. Not financial advice. Forecasts are probabilistic, not guarantees.</p>
    </div>
  );
}
